<template>
  <div>
    <v-container fluid>
      <!-- Hero Header -->
      <v-card class="hero-header mb-4" elevation="0">
        <div class="hero-content">
          <div class="hero-icon-wrapper">
            <v-icon size="44" color="white">mdi-chart-box-outline</v-icon>
          </div>
          <div class="hero-text">
            <h1 class="hero-title">สถิติภาพรวมข้อมูลลูกเสือวิสามัญอาชีวศึกษา</h1>
            <p class="hero-subtitle">
              ข้อมูลสรุปเพื่อการบริหารและใช้อ้างอิงทั่วไป — สามารถกรองตามภาค/จังหวัด/วิทยาลัยได้
            </p>
          </div>
          <v-spacer></v-spacer>
          <v-btn class="export-btn" large depressed :loading="exporting" @click="exportStats">
            <v-icon left>mdi-file-excel</v-icon>
            ส่งออกสถิติ (Excel)
          </v-btn>
        </div>
      </v-card>

      <!-- ===== ตัวกรอง ===== -->
      <v-card class="pa-4 mb-4" outlined>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.filter_region_ID"
              label="ภาค"
              :items="region"
              item-text="region_name"
              item-value="region_ID"
              clearable
              dense
              @change="onRegionChange"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.filter_province_ID"
              label="จังหวัด"
              :items="filteredProvince"
              item-text="province_name"
              item-value="province_ID"
              clearable
              dense
              @change="loadStats"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.college_code"
              label="วิทยาลัย"
              :items="college"
              item-text="college_name"
              item-value="college_code"
              clearable
              dense
              @change="loadStats"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.scout_level"
              label="ระดับลูกเสือ"
              :items="scout_levels"
              clearable
              dense
              @change="loadStats"
            ></v-select>
          </v-col>
        </v-row>
      </v-card>

      <!-- ===== การ์ดสรุป ===== -->
      <v-row class="mb-2">
        <v-col cols="12" sm="6" md="3" v-for="card in summaryCards" :key="card.label">
          <v-card class="stat-card" :class="'stat-card-' + card.color" elevation="0">
            <div class="stat-icon">
              <v-icon color="white" size="26">{{ card.icon }}</v-icon>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ card.value }}</span>
              <span class="stat-label">{{ card.label }}</span>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== กราฟ ===== -->
      <v-row>
        <v-col cols="12" md="5">
          <v-card class="pa-3 chart-card" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">สัดส่วนตามระดับลูกเสือ</div>
            <apexchart
              v-if="chartByLevel.series.length"
              type="donut"
              height="320"
              :options="chartByLevel.options"
              :series="chartByLevel.series"
            ></apexchart>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูล</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="7">
          <v-card class="pa-3 chart-card" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">จำนวนตามตำแหน่งทางลูกเสือ</div>
            <apexchart
              v-if="chartByPosition.series[0] && chartByPosition.series[0].data.length"
              type="bar"
              height="320"
              :options="chartByPosition.options"
              :series="chartByPosition.series"
            ></apexchart>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูล</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card class="pa-3 chart-card" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">จำนวนบุคลากรด้านลูกเสือตามภาค</div>
            <apexchart
              v-if="chartByRegion.series[0] && chartByRegion.series[0].data.length"
              type="bar"
              height="320"
              :options="chartByRegion.options"
              :series="chartByRegion.series"
            ></apexchart>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูล</div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-snackbar v-model="snackbar.show" :timeout="4000" :color="snackbar.color" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "PersonnelScoutStats",
  components: { apexchart: VueApexCharts },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      exporting: false,
      college: [],
      region: [],
      province: [],
      scout_levels: [
        "ลูกเสือสำรอง",
        "ลูกเสือสามัญ",
        "ลูกเสือสามัญรุ่นใหญ่",
        "ลูกเสือวิสามัญ"
      ],

      filters: {
        college_code: null,
        scout_level: null,
        filter_province_ID: null,
        filter_region_ID: null
      },

      statsRaw: { by_level: [], by_position: [], by_region: [], instructor_total: 0 },
      summaryCards: [],

      chartByLevel: { series: [], options: {} },
      chartByPosition: { series: [{ name: "จำนวน", data: [] }], options: {} },
      chartByRegion: { series: [{ name: "จำนวน", data: [] }], options: {} },

      snackbar: { show: false, color: "", text: "" }
    };
  },

  computed: {
    filteredProvince() {
      if (!this.filters.filter_region_ID) return this.province;
      return this.province.filter(p => p.region_ID === this.filters.filter_region_ID);
    }
  },

  async mounted() {
    await Promise.all([this.loadCollege(), this.loadRegionProvince()]);
    await this.loadStats();
  },

  methods: {
    onRegionChange() {
      this.filters.filter_province_ID = null;
      this.loadStats();
    },

    async loadCollege() {
      let result = await this.$http.post("college.php", { ApiKey: this.ApiKey });
      this.college = result.data;
    },

    async loadRegionProvince() {
      let [regionRes, provinceRes] = await Promise.all([
        this.$http.post("region.php", { ApiKey: this.ApiKey }),
        this.$http.post("province.php", { ApiKey: this.ApiKey })
      ]);
      this.region = regionRes.data;
      this.province = provinceRes.data;
    },

    async loadStats() {
      this.loading = true;
      try {
        let result = await this.$http.post("personnel_scout_report.php", {
          ApiKey: this.ApiKey,
          action: "stats",
          ...this.filters
        });
        const d = result.data;
        this.statsRaw = d;

        const totalAll = (d.by_level || []).reduce((s, r) => s + Number(r.total), 0);

        this.summaryCards = [
          {
            label: "บุคลากรลูกเสือทั้งหมด",
            value: totalAll,
            icon: "mdi-account-group",
            color: "1"
          },
          {
            label: "วิทยากรลูกเสือ",
            value: d.instructor_total || 0,
            icon: "mdi-account-star",
            color: "2"
          },
          {
            label: "ระดับที่พบมากที่สุด",
            value: (d.by_level && d.by_level[0] && d.by_level[0].label) || "-",
            icon: "mdi-shield-star-outline",
            color: "3"
          },
          {
            label: "ตำแหน่งที่พบมากที่สุด",
            value: (d.by_position && d.by_position[0] && d.by_position[0].label) || "-",
            icon: "mdi-account-tie-outline",
            color: "4"
          }
        ];

        this.chartByLevel = {
          series: (d.by_level || []).map(r => Number(r.total)),
          options: {
            labels: (d.by_level || []).map(r => r.label || "ไม่ระบุ"),
            legend: { position: "bottom" },
            colors: ["#B3151B", "#424242", "#E57373", "#9E9E9E", "#8E1014"],
            dataLabels: { enabled: true }
          }
        };

        this.chartByPosition = {
          series: [{ name: "จำนวน", data: (d.by_position || []).map(r => Number(r.total)) }],
          options: {
            chart: { toolbar: { show: false } },
            xaxis: { categories: (d.by_position || []).map(r => r.label || "ไม่ระบุ") },
            plotOptions: { bar: { borderRadius: 4, horizontal: true } },
            colors: ["#B3151B"],
            dataLabels: { enabled: false }
          }
        };

        this.chartByRegion = {
          series: [{ name: "จำนวน", data: (d.by_region || []).map(r => Number(r.total)) }],
          options: {
            chart: { toolbar: { show: false } },
            xaxis: { categories: (d.by_region || []).map(r => r.label || "ไม่ระบุ") },
            plotOptions: { bar: { borderRadius: 4, horizontal: false } },
            colors: ["#B3151B"],
            dataLabels: { enabled: false }
          }
        };
      } catch (e) {
        this.showSnackbar("error", "โหลดข้อมูลสถิติไม่สำเร็จ");
      } finally {
        this.loading = false;
      }
    },

    async exportStats() {
      this.exporting = true;
      try {
        const d = this.statsRaw;
        const workbook = XLSX.utils.book_new();

        const summarySheet = XLSX.utils.json_to_sheet([
          { รายการ: "บุคลากรลูกเสือทั้งหมด", จำนวน: (d.by_level || []).reduce((s, r) => s + Number(r.total), 0) },
          { รายการ: "วิทยากรลูกเสือ", จำนวน: d.instructor_total || 0 }
        ]);
        XLSX.utils.book_append_sheet(workbook, summarySheet, "สรุป");

        const levelSheet = XLSX.utils.json_to_sheet(
          (d.by_level || []).map(r => ({ ระดับลูกเสือ: r.label, จำนวน: Number(r.total) }))
        );
        XLSX.utils.book_append_sheet(workbook, levelSheet, "ตามระดับ");

        const positionSheet = XLSX.utils.json_to_sheet(
          (d.by_position || []).map(r => ({ ตำแหน่งทางลูกเสือ: r.label, จำนวน: Number(r.total) }))
        );
        XLSX.utils.book_append_sheet(workbook, positionSheet, "ตามตำแหน่ง");

        const regionSheet = XLSX.utils.json_to_sheet(
          (d.by_region || []).map(r => ({ ภาค: r.label, จำนวน: Number(r.total) }))
        );
        XLSX.utils.book_append_sheet(workbook, regionSheet, "ตามภาค");

        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(
          new Blob([wbout], { type: "application/octet-stream" }),
          `สถิติข้อมูลลูกเสือ_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
      } catch (e) {
        this.showSnackbar("error", "ส่งออกสถิติไม่สำเร็จ");
      } finally {
        this.exporting = false;
      }
    },

    showSnackbar(color, text) {
      this.snackbar = { show: true, color, text };
    }
  }
};
</script>

<style scoped>
.hero-header {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
  border-radius: 16px;
  padding: 28px 32px;
}

.hero-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hero-title {
  color: #fff;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 4px;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.92rem;
  margin: 0;
  max-width: 640px;
}

.export-btn {
  background: #fff !important;
  color: #1b5e20 !important;
  border-radius: 10px;
}

.chart-card {
  border-radius: 12px;
}

.stat-card {
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #fff;
}

.stat-card-1 {
  background: linear-gradient(135deg, #2e7d32, #2e7d32);
}
.stat-card-2 {
  background: linear-gradient(135deg, #455a64, #455a64);
}
.stat-card-3 {
  background: linear-gradient(135deg, #3B82C4 0%, #2B6CB0 100%);
}
.stat-card-4 {
  background: linear-gradient(135deg, #A86400, #A86400);
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.78rem;
  opacity: 0.9;
}
</style>
